import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import * as CustomersActions from '../actions/customers.actions';
import { LaravelCustomerService } from '../../commons/services/laravel/laravel-customer.service';
import { NewCustomerComponent } from '../../modules/home/modules/customers/components/new-customer/new-customer.component';
import { getCustomerDialogId, getCustomersTableState } from '../selectors/customers.selectors';
import { getSelectedStoreTenant, getTenants } from '../selectors/tenant.selectors';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class CustomersEffects {
  error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CustomersActions.deleteCustomerFailed,
          CustomersActions.saveCustomerFailed,
          CustomersActions.loadCustomersFailed,
        ),
        tap(({ error }) => {
          if (error) {
            this.alertService.showErrorMessage('Errore', error);
          }
        })
      ),
    { dispatch: false }
  );

  loadCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.loadCustomers),
      withLatestFrom(this.store$.select(getSelectedStoreTenant)),
      map(([{ page, perPage, order, direction, filters, includes }, tenant]) => {
        const f = {...filters};
        if (filters.onlyCurrentTenant) {
          f.defaultTenantId = tenant.id
        }
        return { page, perPage, order, direction, filters: f, includes };
      }),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {

        return this.customersService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) => {
              // console.log("🔍 Risultato API:", result.data); // ✅ Debug qui
              return CustomersActions.loadCustomersCompleted({
                customers: result.data,
                currentPage: page,
                total: result.total,
                perPage,
                order,
                direction,
                filters,
                includes
              });
            }),

            catchError((error) => of(CustomersActions.loadCustomersFailed({ error })))
          );
      })
    )
  );

  changePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.changePage),
      withLatestFrom(this.store$.select(getCustomersTableState)),
      map(([{ page, size }, { order, direction, filters, includes }]) =>
        CustomersActions.loadCustomers({
          page: page,
          perPage: size,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  changeSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.changeSort),
      withLatestFrom(this.store$.select(getCustomersTableState)),
      map(([action, { currentPage, perPage, filters, includes }]) =>
        CustomersActions.loadCustomers({
          page: currentPage,
          perPage: perPage,
          order: action.order,
          direction: action.direction,
          filters,
          includes,
        })
      )
    )
  );

  changeFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.changeFilters),
      withLatestFrom(this.store$.select(getCustomersTableState)),
      map(([{ filters }, { currentPage, perPage, order, direction, includes }]) =>
        CustomersActions.loadCustomers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  editCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.editCustomer),
      //withLatestFrom(this.store$.select(getTenants)),
      map((action) => {
        let dialogRef = this.dialog.open(NewCustomerComponent, {
          data: { user: action.customer },
          panelClass: 'full-screen-dialog'
        });
        return CustomersActions.customerDialogOpened({ dialogId: dialogRef.id });
      })
    )
  );

  // saveCustomer$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CustomersActions.saveCustomer),
  //     switchMap(({ customer }) =>
  //       //this.customersService.update(customer.id, customer.name, customer.surname, customer.phone, customer.email, customer.notes, customer.active, customer.fidelityCardPoints, customer.defaultTenantId, customer.user_roles).pipe(
  //       this.customersService.upsert(customer.toDTO()).pipe(
  //         map((result) => CustomersActions.saveCustomerCompleted({ customer: result })),
  //         catchError((error) => of(CustomersActions.saveCustomerFailed({ error })))
  //       )
  //     )
  //   )
  // );

  saveCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.saveCustomer),
      withLatestFrom(this.store$.select(getSelectedStoreTenant)),
      mergeMap(([{ customer }, selectedTenant]) => {
        let tenantId = selectedTenant ? selectedTenant.id : null;
        let customerSave = {
          ...customer.toDTO(),
          default_tenant_id: tenantId
        };
        return this.customersService.upsert(customerSave).pipe(
            map((result) =>
              CustomersActions.saveCustomerCompleted({ customer: result })
            ),
            // catchError((error) =>
            // of(CustomersActions.saveCustomerFailed({ error }))
            // ),
            tap((action) => {
              if (action.type === CustomersActions.saveCustomerCompleted.type) {
                this.alertService.showConfirmMessage(`Cliente ${action.customer.name} salvato con successo`);
                this.store$.dispatch(CustomersActions.closeCustomerDialog());
              } else {
                this.store$.dispatch(CustomersActions.saveCustomerFailed({ error: Error }));
              }
            }),
          );
        })
      )
    );

  // onSaveCompleted$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CustomersActions.saveCustomerCompleted),
  //     tap(({ customer }) =>
  //       this.alertService.showConfirmMessage(`Cliente ${customer.name} salvato con successo`)
  //     ),
  //     tap(() => CustomersActions.closeCustomerDialog())
  //   ), { dispatch: false }
  // );

  deleteCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.deleteCustomer),
      switchMap(({ customer }) =>
        this.alertService
          .showConfirmDialog(
            `Conferma eliminazione`,
            `Sei sicuro di voler eliminare il cliente ${customer.name} ${customer.surname}?`
          )
          .pipe(
            mergeMap((confirm) =>
              confirm
                ? this.customersService.delete(customer.id).pipe(
                  map(() => CustomersActions.deleteCustomerCompleted({ customer })),
                  catchError((error) => of(CustomersActions.deleteCustomerFailed({ error })))
                )
                : of(CustomersActions.deleteCustomerCancelled())

            )
          )

      )
    )
  );

    requestCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(CustomersActions.requestCloseCustomersDialog),
          switchMap(() => {
            const confirmDialogRef = this.dialog.open(DialogComponent, {
              disableClose: false
            });

            confirmDialogRef.componentInstance.title = "Chiudi";
            confirmDialogRef.componentInstance.confirmMessage =
              "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

            return confirmDialogRef.afterClosed().pipe(
              map(result =>
                result
                  ? CustomersActions.confirmCloseCustomersDialog()
                  : CustomersActions.cancelCloseCustomersDialog()
              )
            );
          })
        )
      );


      confirmCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(CustomersActions.confirmCloseCustomersDialog),
          map(() => CustomersActions.closeCustomerDialog())
        ), { dispatch: false }
      );

      cancelCloseDialog$ = createEffect(() =>
        this.actions$.pipe(
          ofType(CustomersActions.cancelCloseCustomersDialog),
          map(() => {
            return { type: 'NO_ACTION' };
          })
        )
      );

      closeDialog$ = createEffect(
        () =>
          this.actions$.pipe(
            ofType(CustomersActions.closeCustomerDialog),
            withLatestFrom(this.store$.select(getCustomerDialogId)),
            tap(([_, dialogId]) => {
              if (dialogId) {
                this.dialog.getDialogById(dialogId).close();
              }
            })
          ),
        { dispatch: false }
      );


  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.deleteCustomerCompleted),
      tap(({ customer }) =>
        this.alertService.showConfirmMessage(`Cliente ${customer.name} eliminato con successo`)
      ),
      map(() => CustomersActions.closeCustomerDialog())
    )
  );

  // closeDialog = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(CustomersActions.closeCustomerDialog),
  //       withLatestFrom(this.store$.select(getCustomerDialogId)),
  //       tap(([_, dialogId]) => {
  //         if (dialogId) {
  //           this.dialog.getDialogById(dialogId)?.close();
  //         }
  //       })
  //     ),
  //   { dispatch: false }
  // );

  reloadAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.saveCustomerCompleted),
      withLatestFrom(this.store$.select(getCustomersTableState)),
      map(([_, { currentPage, perPage, order, direction, filters, includes }]) =>
        CustomersActions.loadCustomers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  reloadAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.deleteCustomerCompleted),
      withLatestFrom(this.store$.select(getCustomersTableState)),
      map(([_, { currentPage, perPage, order, direction, filters, includes }]) =>
        CustomersActions.loadCustomers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private dialog: MatDialog,
    private alertService: AlertService,
    private customersService: LaravelCustomerService
  ) { }
}
